import React, { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from 'react-i18next';
import "./Modal.scss";
import UniversalButton from "components/uiComponents/UniversalButton";
import Select from "components/uiComponents/Select";
import UniversalInput from "components/uiComponents/UniversalInput";
import DatePicker from "components/uiComponents/Datepicker";
import ReactSelect from 'react-select';
import { setCurrentTableCellAC } from "store/actions/table";
import { displayType } from "../../uiComponents/CustomBodyCellV2/model/types";
import {
  isTextOrNumberType,
  isBoolType,
  isDateType,
  isEnumType,
  isMultiEnumType,
  isNumberType,
  getReactSelectStyles,
  booleanOprions,
} from "utils/valueTypeHelpers";

const SetDataModal = ({ table, columns, selectedItems, closeModal, modelId = null }) => {
  const { t } = useTranslation();
  
  // Get rowSelection from localStorage (may use stable keys like 'le:...' or string indices)
  const storageItems = (() => {
    try {
      const { getCurrentNamespace } = require('utils/crossTabSync');
      const currentNs = getCurrentNamespace() || modelId;
      const rowSelectionKey = currentNs ? `rowSelection:${currentNs}` : (modelId ? `rowSelection:${modelId}` : 'rowSelection');
      const raw = localStorage.getItem(rowSelectionKey);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
      return {};
    }
  })();

  const lineElementsArray = [];

  // Build a map of row keys to rows for efficient lookup
  const rowsByKey = new Map();
  (table.rowsData || []).forEach((row, idx) => {
    const ids = Array.isArray(row?.lineElements) ? row.lineElements : [];
    if (ids.length) {
      rowsByKey.set(`le:${ids.join("|")}`, row);
    }
    rowsByKey.set(String(idx), row);
  });

  // Collect lineElements from selected rows
  for (const key in storageItems) {
    if (storageItems[key]) {
      const row = rowsByKey.get(key);
      const lineElements = row?.lineElements;
      if (Array.isArray(lineElements) && lineElements.length) {
        lineElementsArray.push(...lineElements);
      }
    }
  }

  const dispatch = useDispatch();
  const { customPropertiesValues } = useSelector((state) => state.viewer);
  const [value, setValue] = useState(null);
  const [currentOption, setCurrentOption] = useState(null);
  const [multiplechoiceSelectedValue, setMultiplechoiceSelectedValue] = useState([]);
  const [customBoolValue, setCustomBoolValue] = useState(null);

  const isCanSetEmptyValue = useMemo(() => {
    const type = currentOption?.type;
    return isDateType(type) || isEnumType(type);
  }, [currentOption]);

  const options = columns
    .filter((filterItem) => !!filterItem?.meta?.type && filterItem?.meta?.type >= 10)
    .map((item) => ({
      label: item.header,
      value: item.header,
      type: item.meta?.type,
      id: item?.id,
      key: item?.id,
    }));

  const columnMeta = useMemo(
    () => columns.find((c) => c.id === currentOption?.id)?.meta,
    [columns, currentOption?.id]
  );

  const multiplechoiceValuesSource =
    customPropertiesValues?.find((item) => item?.name === currentOption?.key)?.values ||
    columnMeta?.existingValue;

  const multiplechoiceValuesOptions = useMemo(() => {
    return multiplechoiceValuesSource?.map((item) => ({ label: item, value: item })) || [];
  }, [multiplechoiceValuesSource]);

  const handleSetData = () => {
    if (!currentOption) return;

    const type = currentOption.type;
    let newValue;

    if (isEnumType(type)) {
      newValue = Array.isArray(multiplechoiceSelectedValue)
        ? multiplechoiceSelectedValue.map(item => item.value).join('; ')
        : multiplechoiceSelectedValue?.label;
    } else if (isBoolType(type)) {
      newValue = customBoolValue?.value;
    } else {
      newValue = value;
    }

    const itemsToUpdate = lineElementsArray?.length ? lineElementsArray : selectedItems;
    dispatch(setCurrentTableCellAC(currentOption.id, itemsToUpdate, newValue));
    closeModal();
  };

  const handleClearValue = () => {
    if (!currentOption) return;

    const isSelectField = isEnumType(currentOption.type);
    const clearValue = isSelectField ? '' : null;

    dispatch(setCurrentTableCellAC(currentOption.id, selectedItems, clearValue));
    closeModal();
  };

  const reactSelectStyles = getReactSelectStyles();

  const renderField = () => {
    if (!currentOption) {
      return (
        <UniversalInput
          value={value}
          onChangeValue={setValue}
          type="text"
          withFullWidth={true}
          disabled={true}
        />
      );
    }

    const type = currentOption.type;

    if (isTextOrNumberType(type)) {
      const inputType = isNumberType(type) ? 'number' : 'text';
      return (
        <UniversalInput
          value={value}
          onChangeValue={setValue}
          type={inputType}
          withFullWidth={true}
        />
      );
    }

    if (isDateType(type)) {
      return (
        <DatePicker
          value={value}
          onChangeValue={setValue}
        />
      );
    }

    if (isBoolType(type)) {
      return (
        <ReactSelect
          value={customBoolValue}
          onChange={setCustomBoolValue}
          options={booleanOprions}
          isSearchable={false}
          styles={reactSelectStyles}
          classNamePrefix="react-select"
        />
      );
    }

    if (isEnumType(type)) {
      return (
        <ReactSelect
          value={multiplechoiceSelectedValue}
          placeholder={t('please_select_value')}
          onChange={setMultiplechoiceSelectedValue}
          options={multiplechoiceValuesOptions}
          isMulti={isMultiEnumType(type)}
          isSearchable={false}
          styles={reactSelectStyles}
          classNamePrefix="react-select"
        />
      );
    }

    return (
      <UniversalInput
        value={value}
        onChangeValue={setValue}
        type={type === displayType.number ? 'number' : "text"}
        withFullWidth={true}
        disabled={!currentOption}
      />
    );
  };

  return (
    <div className="modal modal-set-data">
      <span className="modal__header">{t('set_data')}</span>
      <div className="modal__content">
        <div>
          <p className="modal__content-field-title">{t('column')}</p>
          <Select
            selectedValue={currentOption}
            setSelectedValue={setCurrentOption}
            isBold={false}
            selectSize={"md"}
            options={options}
          />
        </div>
        <div>
          <p className="modal__content-field-title">{t('value')}</p>
          {renderField()}
        </div>
      </div>
      <div className="modal__footer">
        {isCanSetEmptyValue && (
          <UniversalButton
            type="button"
            label={t('set_empty_values')}
            size="lg"
            labelColor="blue"
            labelHoverColor="blue"
            backgroundColor="white"
            backgroundHoverColor="blue-light"
            onClick={handleClearValue}
          />
        )}
        <div className="modal__footer__controls">
          <UniversalButton
            type="button"
            label={t('cancel')}
            size="lg"
            labelColor="blue"
            labelHoverColor="blue"
            backgroundColor="white"
            backgroundHoverColor="blue-light"
            onClick={closeModal}
          />
          <UniversalButton
            type="button"
            label={t('apply')}
            size="lg"
            labelColor="white"
            labelHoverColor="white"
            backgroundColor="orange"
            backgroundHoverColor="orange-light"
            onClick={handleSetData}
          />
        </div>
      </div>
    </div>
  );
};

export default SetDataModal;